import React, { useState, useEffect, useRef } from 'react';

/**
 * Smart voice player that reads the FULL section:
 * - Section title
 * - Agent script (intro)
 * - All step-by-step instructions
 * 
 * Uses browser Speech Synthesis API. Free.
 */
export default function VoicePlayer({ section, os }) {
  const [status, setStatus] = useState('idle');
  const [isSupported, setIsSupported] = useState(true);
  const utteranceRef = useRef(null);

  useEffect(() => {
    if (!window.speechSynthesis) {
      setIsSupported(false);
    }
  }, []);

  // Reset when section changes
  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setStatus('idle');
  }, [section?.id]);

  // Build the complete script from section data
  const buildFullScript = () => {
    if (!section) return '';

    const parts = [];

    // Start with title announcement
    if (section.title) {
      parts.push(`Section: ${section.title}.`);
    }

    // Add agent script (intro)
    if (section.agentScript) {
      parts.push(section.agentScript);
    }

    // Add step-by-step instructions
    const steps = (section.steps || []).filter(
      (step) => !step.os || step.os === 'all' || step.os === os
    );

    if (steps.length > 0) {
      parts.push(`Here are the step by step instructions.`);
      steps.forEach((step, index) => {
        const stepNum = index + 1;
        let stepText = `Step ${stepNum}: ${step.text}.`;
        if (step.detail) {
          stepText += ` ${step.detail}.`;
        }
        parts.push(stepText);
      });
    }

    // Add closing
    parts.push(`That completes this section. Click Mark as Covered to continue.`);

    // Join all parts with pauses
    const fullText = parts.join(' ');

    // Clean up special characters that may mess up speech
    return fullText
      .replace(/[""]/g, '')
      .replace(/—/g, ', ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const speak = () => {
    if (!window.speechSynthesis) return;

    const fullText = buildFullScript();
    if (!fullText) return;

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(fullText);
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Pick best available voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice =
      voices.find((v) => v.name.includes('Samantha')) ||
      voices.find((v) => v.name.includes('Google US English')) ||
      voices.find((v) => v.lang === 'en-US') ||
      voices[0];
    if (preferredVoice) utterance.voice = preferredVoice;

    utterance.onstart = () => setStatus('playing');
    utterance.onend = () => setStatus('idle');
    utterance.onerror = () => setStatus('idle');

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const pause = () => {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.pause();
      setStatus('paused');
    }
  };

  const resume = () => {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setStatus('playing');
    }
  };

  const restart = () => {
    window.speechSynthesis.cancel();
    setTimeout(() => speak(), 100);
  };

  const stop = () => {
    window.speechSynthesis.cancel();
    setStatus('idle');
  };

  if (!isSupported) {
    return (
      <div style={{ fontSize: 14, color: '#8a8886', fontStyle: 'italic', padding: '6px 10px' }}>
        Voice not supported in this browser
      </div>
    );
  }

  const primaryBtn = {
    background: '#009245',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '8px 14px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: "'Segoe UI', -apple-system, sans-serif",
  };

  const secondaryBtn = {
    ...primaryBtn,
    background: '#f3f2f1',
    color: '#323130',
    border: '1px solid #d2d0ce',
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginTop: 10,
      padding: '10px 12px',
      background: 'rgba(98, 100, 167, 0.08)',
      borderRadius: 6,
      border: '1px solid rgba(98, 100, 167, 0.2)',
      flexWrap: 'wrap',
    }}>
      {status === 'idle' && (
        <button onClick={speak} style={primaryBtn} title="Agent will read everything for this section">
          ▶ Play Complete Guidance
        </button>
      )}

      {status === 'playing' && (
        <>
          <button onClick={pause} style={primaryBtn} title="Pause">
            ⏸ Pause
          </button>
          <span style={{
            fontSize: 14,
            color: '#009245',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}>
            <span style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#009245',
              display: 'inline-block',
            }} />
            Agent speaking...
          </span>
        </>
      )}

      {status === 'paused' && (
        <>
          <button onClick={resume} style={primaryBtn} title="Resume">
            ▶ Resume
          </button>
          <span style={{ fontSize: 14, color: '#8a8886', fontWeight: 600 }}>
            Paused
          </span>
        </>
      )}

      {(status === 'playing' || status === 'paused') && (
        <>
          <button onClick={restart} style={secondaryBtn} title="Restart from beginning">
            ↻ Restart
          </button>
          <button onClick={stop} style={secondaryBtn} title="Stop">
            ✕ Stop
          </button>
        </>
      )}
    </div>
  );
}
